<template>
  <UiCard
    :class="['product-card', variant === 'horizontal' ? 'product-card--horizontal' : '']"
    @click="goToProduct"
  >
    <div class="product-card__media">
      <img
        :src="cardImage"
        :alt="product.name"
        loading="lazy"
        decoding="async"
        class="product-card__image"
        @error="handleImageError"
      />
      <div class="product-card__badges">
        <UiBadge v-if="product.isNew" variant="accent">Новинка</UiBadge>
        <UiBadge v-if="product.isBestseller" variant="accent">Хит продаж</UiBadge>
        <UiBadge
          v-if="product.originalPrice && product.originalPrice > product.price"
          variant="accent"
        >
          Скидка
        </UiBadge>
      </div>
      <button
        type="button"
        class="product-card__favorite-btn"
        :class="{ 'is-active': isFavorite }"
        @click.stop="toggleFavorite"
        :title="favoriteButtonText"
        data-tutorial="favorite-btn"
      >
        <span class="product-card__favorite-icon">{{ isFavorite ? '♥' : '♡' }}</span>
      </button>
      <button
        v-if="hasRealImage"
        type="button"
        class="product-card__quick-view-btn"
        @click.stop="quickView"
      >
        Быстрый просмотр
      </button>
    </div>

    <div class="product-card__content">
      <div v-if="metaLine" class="product-card__meta">
        {{ metaLine }}
      </div>

      <h3 class="product-card__title">
        {{ product.name }}
      </h3>

      <div v-if="product.sku" class="product-card__sku">
        <span class="product-card__sku-label">Артикул: {{ product.sku }}</span>
        <button
          type="button"
          class="product-card__sku-copy"
          title="Скопировать артикул"
          @click.stop="copySku"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
        </button>
      </div>

      <p v-if="variant !== 'horizontal' && cardDescription" class="product-card__description">
        {{ cardDescription }}
      </p>

      <div v-if="product.rating > 0 || product.reviewCount > 0" class="product-card__rating">
        <UiBadge variant="neutral">{{ product.rating }}</UiBadge>
        <span class="product-card__muted">({{ product.reviewCount }})</span>
      </div>

      <div class="product-card__footer">
        <div>
          <div class="product-card__price">{{ formatPriceWithCents(product.price) }}</div>
          <div
            v-if="product.originalPrice && product.originalPrice > product.price"
            class="product-card__price-old"
          >
            {{ formatPriceWithCents(product.originalPrice) }}
          </div>
          <div
            v-if="product.originalPrice && product.originalPrice > product.price"
            class="product-card__discount"
          >
            -{{ Math.round((1 - product.price / product.originalPrice) * 100) }}%
          </div>
        </div>
        <div class="product-card__stock" v-if="variant !== 'horizontal'">
          {{ product.inStock ? 'В наличии' : 'Нет в наличии' }}
        </div>
      </div>

      <div
        v-if="product.minimumOrder && product.minimumOrder > 1"
        class="product-card__minimum-order"
      >
        Минимальный заказ: {{ product.minimumOrder }} {{ product.unit?.symbol || 'шт.' }}
      </div>

      <UiButton
        v-if="!isSupplier"
        :class="['product-card__cart-btn', { 'is-in-cart': isInCart && product.inStock }]"
        variant="secondary"
        :disabled="!product.inStock || addingToCart"
        @click.stop="addToCart"
      >
        <svg
          class="product-card__cart-btn-icon"
          :class="{ 'product-card__cart-btn-icon--hidden': !(isInCart && product.inStock) }"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
        {{ !product.inStock ? 'Нет в наличии' : isInCart ? 'Убрать из корзины' : 'В корзину' }}
      </UiButton>
    </div>
  </UiCard>

  <ProductQuickView v-model="showQuickView" :product="product" />
  <AuthPromptModal />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useSnackbar } from '@/modules/common/public'
import { useRouter } from 'vue-router'
import { productThumbnailUrl } from '@/shared/lib/imageVariants'
import { useCartActions } from '@/modules/order/public'
import { useFavoritesActions } from '@/modules/profile/public'
import { useAuthSession } from '@/modules/auth/public'
import type { Product } from '@/types/catalog'
import { formatPriceWithCents } from '@/utils/price'
import ProductQuickView from './ProductQuickView.vue'
import { AuthPromptModal } from '@/modules/common/public'
import UiBadge from '@/shared/ui/UiBadge.vue'
import UiButton from '@/shared/ui/UiButton.vue'
import UiCard from '@/shared/ui/UiCard.vue'

interface Props {
  product: Product
  variant?: 'default' | 'horizontal'
  context?: 'catalog' | 'favorites'
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
  context: 'catalog',
})

const favoriteButtonText = computed(() => {
  if (props.context === 'favorites') return 'Убрать из избранного'
  return isFavorite.value ? 'В избранном' : 'В избранное'
})

const router = useRouter()
const cartActions = useCartActions()
const favoritesActions = useFavoritesActions()
const authSession = useAuthSession()
const isSupplier = computed(() => authSession.isSupplier.value)
const {
  showSuccess: showSnackbarSuccess,
  showError: showSnackbarError,
  showWarning: showSnackbarWarning,
  showInfo: showSnackbarInfo,
} = useSnackbar()

const addingToCart = ref(false)
const showQuickView = ref(false)
const fallbackImage = '/placeholder-product.svg'

const isInCart = computed(() => cartActions.isInCart(props.product.id))
const isFavorite = computed(() => favoritesActions.isFavorite(props.product.id))

const primaryImage = computed(() => {
  return props.product.images?.[0] || props.product.imageUrl || '/placeholder-product.svg'
})

// Миниатюра есть не всегда (генерация могла упасть) - при ошибке откатываемся к оригиналу
const thumbnailFailed = ref(false)
const cardImage = computed(() => {
  const thumbnail = thumbnailFailed.value ? null : productThumbnailUrl(primaryImage.value)
  return thumbnail ?? primaryImage.value
})

const hasRealImage = computed(() => {
  const url = primaryImage.value
  return url !== fallbackImage && url.length > 0 && !url.includes('placehold.co')
})

const displayCategoryManufacturer = computed(() => {
  const cat = (props.product.categoryName ?? '').trim()
  const man = (props.product.manufacturerName ?? '').trim()
  return (cat && cat !== '-') || (man && man !== '-')
})

const metaLine = computed(() => {
  if (!displayCategoryManufacturer.value) {
    return ''
  }

  const parts = [
    (props.product.categoryName ?? '').trim(),
    (props.product.manufacturerName ?? '').trim(),
  ].filter((value) => value !== '' && value !== '-')

  return parts.join(' • ')
})

const cardDescription = computed(() => {
  const candidate = (props.product.shortDescription || props.product.description || '').trim()
  const productName = (props.product.name || '').trim()

  if (candidate === '' || candidate === productName) {
    return ''
  }

  return candidate
})

async function copySku() {
  if (!props.product.sku) return

  try {
    await navigator.clipboard.writeText(props.product.sku)
    showSnackbarSuccess('Артикул скопирован в буфер обмена')
  } catch {
    showSnackbarError('Не удалось скопировать артикул')
  }
}

function goToProduct() {
  router.push(`/product/${props.product.id}`)
}

async function addToCart() {
  if (!props.product.inStock || addingToCart.value) return

  addingToCart.value = true

  try {
    if (isInCart.value) {
      await cartActions.removeItem(props.product.id)
      showSnackbarInfo(`Товар "${props.product.name}" удалён из корзины`)
      return
    }

    await cartActions.addItem({
      productId: props.product.id,
      name: props.product.name,
      price: props.product.price,
      sku: props.product.sku,
      image: primaryImage.value,
      quantity: props.product.minimumOrder ?? 1,
    })
    showSnackbarSuccess(
      `Товар "${props.product.name}" добавлен в корзину`,
      4000,
      { label: 'Перейти в корзину', to: '/cart' },
    )
  } catch (error) {
    const message = error instanceof Error ? error.message : ''
    const minMatch = message.match(/Минимальный заказ[^:]*:\s*(\d+)/i)

    if (minMatch?.[1]) {
      showSnackbarWarning(`Для добавления товара откройте карточку и укажите минимум ${minMatch[1]} шт.`, 6000, {
        label: 'Открыть товар',
        onClick: () => router.push(`/product/${props.product.id}`),
      })
      return
    }

    showSnackbarError(message || 'Не удалось добавить товар в корзину')
  } finally {
    addingToCart.value = false
  }
}

function toggleFavorite() {
  favoritesActions
    .toggleFavorite(props.product)
    .then((result) => {
      if (result.added) {
        showSnackbarSuccess('Товар добавлен в избранное', 4000, {
          label: 'Перейти в избранное',
          to: '/favorites',
        })
      } else if (result.removed) {
        showSnackbarInfo('Товар удален из избранного')
      } else if (result.isFavorite) {
        showSnackbarSuccess('Товар добавлен в избранное', 4000, {
          label: 'Перейти в избранное',
          to: '/favorites',
        })
      } else {
        showSnackbarInfo('Товар удален из избранного')
      }
    })
    .catch(() => {
      showSnackbarError('Ошибка при работе с избранным')
    })
}

function quickView() {
  showQuickView.value = true
}

function handleImageError(event: Event) {
  const target = event.target as HTMLImageElement | null
  if (!target) return
  if (!thumbnailFailed.value && productThumbnailUrl(primaryImage.value) !== null) {
    thumbnailFailed.value = true
    return
  }
  if (!target.src.endsWith(fallbackImage)) {
    target.src = fallbackImage
  }
}
</script>

<style scoped>
.product-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;
  cursor: pointer;
  width: 100%;
  min-height: 100%;
}

.product-card--horizontal {
  grid-template-columns: 200px 1fr;
  align-items: stretch;
}

.product-card__media {
  position: relative;
  overflow: hidden;
  border-radius: 10px;
}

.product-card__image {
  width: 100%;
  height: 220px;
  object-fit: cover;
  border-radius: 10px;
  transition: transform 0.2s ease;
}

.product-card--horizontal .product-card__media {
  height: 100%;
  min-height: 170px;
}

.product-card--horizontal .product-card__image {
  height: 100%;
}

.product-card__badges {
  position: absolute;
  top: 8px;
  left: 8px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.product-card__favorite-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  color: #586578;
  transition:
    background 0.2s ease,
    color 0.2s ease,
    transform 0.2s ease;
  z-index: 2;
}

.product-card__favorite-btn:hover {
  background: #fff;
  color: var(--color-accent);
  transform: scale(1.1);
}

.product-card__favorite-btn.is-active {
  color: #ef4444;
}

.product-card__favorite-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.product-card__quick-view-btn {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%) translateY(6px);
  opacity: 0;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
  background: rgba(20, 51, 94, 0.9);
  border: none;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  pointer-events: none;
  z-index: 2;
}

.product-card:hover .product-card__quick-view-btn {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
  pointer-events: auto;
}

.product-card:hover .product-card__image {
  transform: scale(1.02);
}

.product-card__content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.product-card__sku {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #586578;
  line-height: 1.35;
  min-height: 17px;
}

.product-card__sku-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-card__sku-copy {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  margin: 0;
  border: none;
  background: transparent;
  color: #586578;
  cursor: pointer;
  border-radius: 4px;
  line-height: 1;
  flex-shrink: 0;
  transition:
    color 0.15s ease,
    background-color 0.15s ease;
}

.product-card__sku-copy:hover {
  color: var(--color-accent);
  background-color: #eff6ff;
}

.product-card__sku-copy:active {
  color: var(--color-accent-hover);
  background-color: #dbeafe;
}

.product-card__sku-copy svg {
  display: block;
}

.product-card__meta {
  font-size: 12px;
  color: #586578;
  line-height: 1.35;
  min-height: 17px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-card__title {
  font-size: 16px;
  margin: 0;
  line-height: 1.3;
  word-break: break-word;
  min-height: 42px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.product-card__description {
  font-size: 13px;
  color: #475569;
  min-height: 34px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.product-card__rating {
  display: flex;
  align-items: center;
  gap: 8px;
}

.product-card__muted {
  font-size: 12px;
  color: #586578;
}

.product-card__footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
}

.product-card__price {
  font-weight: 700;
  font-size: 18px;
}

.product-card__price-old {
  font-size: 12px;
  text-decoration: line-through;
  color: #586578;
}

.product-card__discount {
  font-size: 12px;
  color: #ef4444;
}

.product-card__stock {
  font-size: 12px;
  color: var(--color-success);
  text-align: right;
}

.product-card__minimum-order {
  font-size: 12px;
  color: #586578;
}

.product-card__cart-btn-icon {
  width: 16px;
  height: 16px;
  margin-right: 6px;
  flex-shrink: 0;
}

.product-card__cart-btn-icon--hidden {
  visibility: hidden;
}

.product-card__cart-btn.is-in-cart {
  background: var(--color-accent);
  border-color: var(--color-accent);
  color: #fff;
}

.product-card__cart-btn.is-in-cart:hover {
  background: var(--color-accent-hover);
  border-color: var(--color-accent-hover);
  color: #fff;
}

button.product-card__cart-btn:disabled {
  background: #f1f5f9;
  border-color: #e2e8f0;
  color: #94a3b8;
  opacity: 1;
  cursor: not-allowed;
}

@media (max-width: 600px) {
  .product-card--horizontal {
    grid-template-columns: minmax(0, 1fr);
  }

  .product-card--horizontal .product-card__image {
    height: 220px;
  }

  .product-card__actions {
    opacity: 1;
    transform: none;
    pointer-events: auto;
  }
}
</style>
